import React, { useState } from 'react';
import {
  Box,
  CircularProgress,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { fieldLabelSx, buildAdornmentTextFieldSx } from '../common/formFieldStyles';
import StoreService from '../../services/storeService';
import {
  STORE_SLUG_MAX_LENGTH,
  getSlugAvailabilityErrorKey,
  getSlugFormatErrorKey,
  getStoreSlugHostSuffix,
  sanitizeSlugInput,
} from '../../utils/storeSlugUtils';
import { BRAND } from '../../theme/brandTokens';

const StoreSlugField = ({
  value,
  onChange,
  error,
  setErrors,
  required = true,
  currentSlug,
}) => {
  const { t } = useTranslation();
  const [availability, setAvailability] = useState('idle');
  const suffix = getStoreSlugHostSuffix();

  const clearSlugError = () => {
    if (!setErrors) {
      return;
    }

    setErrors((prev) => (
      prev.slug ? { ...prev, slug: undefined } : prev
    ));
  };

  const setSlugError = (key) => {
    if (!setErrors) {
      return;
    }

    setErrors((prev) => ({ ...prev, slug: t(key) }));
  };

  const handleChange = (event) => {
    setAvailability('idle');
    clearSlugError();
    onChange(sanitizeSlugInput(event.target.value));
  };

  const handleBlur = async () => {
    const formatKey = getSlugFormatErrorKey(value);
    if (formatKey) {
      setAvailability('invalid');
      setSlugError(formatKey);
      return;
    }

    if (currentSlug && value === currentSlug) {
      setAvailability('available');
      clearSlugError();
      return;
    }

    setAvailability('checking');
    try {
      const result = await StoreService.checkSlugAvailability({
        slug: value,
      });
      if (result?.available) {
        setAvailability('available');
        clearSlugError();
        return;
      }

      const errorKey = getSlugAvailabilityErrorKey(result?.reason);
      setAvailability('unavailable');
      setSlugError(errorKey);
    } catch (availabilityError) {
      console.error('Failed to check slug availability:', availabilityError);
      setAvailability('unavailable');
      setSlugError('storeRegistration.validation.slugCheckFailed');
    }
  };

  const hasError = Boolean(error) || availability === 'invalid' || availability === 'unavailable';
  const showAvailable = availability === 'available' && !error;

  return (
    <Box>
      <Typography variant="subtitle2" sx={fieldLabelSx}>
        {required ? `${t('storeRegistration.basicInfo.slug')} *` : t('storeRegistration.basicInfo.slug')}
      </Typography>
      <TextField
        fullWidth
        value={value || ''}
        onChange={handleChange}
        onBlur={handleBlur}
        error={hasError}
        inputProps={{
          maxLength: STORE_SLUG_MAX_LENGTH,
          autoComplete: 'off',
          spellCheck: false,
          'aria-label': t('storeRegistration.basicInfo.slug'),
        }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end" disableTypography>
              <Typography component="span" sx={{ fontWeight: 600, fontSize: 16, color: BRAND.textMuted, whiteSpace: 'nowrap' }}>
                {suffix}
              </Typography>
            </InputAdornment>
          ),
        }}
        sx={buildAdornmentTextFieldSx(hasError)}
      />
      {availability === 'checking' ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.75 }}>
          <CircularProgress size={14} sx={{ color: BRAND.accent }} />
          <Typography variant="caption" sx={{ color: BRAND.textMuted }}>
            {t('storeRegistration.slug.checking')}
          </Typography>
        </Box>
      ) : hasError ? (
        <Typography variant="caption" sx={{ color: '#f44336', mt: 0.75, display: 'block' }}>
          {error || t('storeRegistration.validation.slugInvalid')}
        </Typography>
      ) : showAvailable ? (
        <Typography variant="caption" sx={{ color: '#2e7d32', mt: 0.75, display: 'block', fontWeight: 600 }}>
          {t('storeRegistration.slug.available')}
        </Typography>
      ) : null}
    </Box>
  );
};

export default StoreSlugField;
